import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { PageHeader, Money, Card, Select, Input, Badge } from '../components/ui';
import { ReportChart } from '../components/charts/ReportChart';
import type { ReportResult } from '../types/api';

type S = ReportResult & { totals?: Record<string, number>; generatedFor?: Record<string, unknown> };
const today = new Date().toISOString().slice(0, 10);
const yearAgo = new Date(Date.now() - 365 * 86_400_000).toISOString().slice(0, 10);

const TABS = {
  pnl: 'Profit & loss',
  'balance-sheet': 'Balance sheet',
  'cash-flow': 'Cash flow',
  'trial-balance': 'Trial balance',
  'general-ledger': 'General ledger',
  'ar-aging': 'Receivables ageing',
  'ap-aging': 'Payables ageing',
} as const;
type Tab = keyof typeof TABS;

/** Which query parameters each statement actually takes. */
const RANGE_TABS = new Set<Tab>(['pnl', 'cash-flow', 'trial-balance', 'general-ledger']);
const AS_AT_TABS = new Set<Tab>(['balance-sheet', 'ar-aging', 'ap-aging']);

export default function StatementsPage() {
  const [tab, setTab] = useState<Tab>('pnl');
  const [from, setFrom] = useState(yearAgo);
  const [to, setTo] = useState(today);
  const [entityId, setEntityId] = useState('');
  const [accountId, setAccountId] = useState('');

  const entities = useQuery({ queryKey: ['entities'], queryFn: async () => (await api.get<Array<Record<string, any>>>('/entities')).data });
  const accounts = useQuery({
    queryKey: ['coa-statement', entityId],
    queryFn: async () => (await api.get<Array<Record<string, any>>>('/accounts', { entityId: entityId || undefined, isPostable: true })).data,
    enabled: tab === 'general-ledger',
  });

  const q: Record<string, unknown> = { entityId: entityId || undefined };
  if (RANGE_TABS.has(tab)) { q.from = from; q.to = to; }
  if (AS_AT_TABS.has(tab)) q.asOf = to;
  if (tab === 'balance-sheet') delete q.from;
  if (tab === 'general-ledger' && accountId) q.accountId = accountId;

  const stmt = useQuery({ queryKey: ['statement', tab, q], queryFn: async () => (await api.get<S>(`/statements/${tab}`, q)).data });
  const trend = useQuery({
    queryKey: ['pnl-trend', from, to, entityId],
    queryFn: async () => (await api.get<Record<string, any>>('/statements/pnl-by-month', { from, to, entityId: entityId || undefined })).data,
    enabled: tab === 'pnl',
  });

  const s = stmt.data;
  const cashFlowUnreconciled = tab === 'cash-flow' && s?.generatedFor?.reconciled === false;

  return (
    <div>
      <PageHeader title="Financial statements" subtitle="Derived from posted journal lines only. Money is integer minor units all the way down." />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {(Object.keys(TABS) as Tab[]).map((t) => (
          <button key={t} onClick={() => setTab(t)}
            className={`rounded-lg px-3 py-1.5 text-sm ${tab === t ? 'bg-ink-900 text-white' : 'border border-ink-200 bg-white text-ink-600 hover:bg-ink-50'}`}>
            {TABS[t]}
          </button>
        ))}
        <span className="mx-1 text-ink-200">|</span>
        <Select value={entityId} onChange={(e) => setEntityId(e.target.value)}>
          <option value="">All companies</option>{entities.data?.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
        </Select>
        {tab === 'general-ledger' && (
          <Select value={accountId} onChange={(e) => setAccountId(e.target.value)}>
            <option value="">Every account</option>{accounts.data?.map((a) => <option key={a.id} value={a.id}>{a.code} {a.name}</option>)}
          </Select>
        )}
        {RANGE_TABS.has(tab) && <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />}
        <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
      </div>

      {s?.generatedFor?.balanced === false && <p className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">This statement does not balance — investigate before relying on it.</p>}
      {s?.generatedFor?.balanced === true && <p className="mb-3 inline-flex items-center gap-2 text-xs text-ink-500"><Badge value="Balanced" tone="good" /> Debits equal credits.</p>}
      {cashFlowUnreconciled && (
        <p className="mb-3 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900">
          The derived cash movement does not equal the actual movement on the bank accounts. The difference is shown at the foot of the statement rather than balanced away.
        </p>
      )}
      {s?.generatedFor?.truncated === true && <p className="mb-3 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900">Showing the first 5,000 lines. Narrow the account or the date range to see the rest.</p>}

      {tab === 'pnl' && s?.totals && (
        <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
          {[['Income', s.totals.incomeMinor], ['Expenses', s.totals.expenseMinor], ['Net profit', s.totals.netMinor]].map(([l, v]) => (
            <Card key={String(l)} className="p-4"><div className="text-xs text-ink-500">{l}</div><div className="mt-1 text-xl font-semibold tabular-nums"><Money minor={v as number} currency={s.currency} /></div></Card>
          ))}
          <Card className="p-4"><div className="text-xs text-ink-500">Margin</div><div className="mt-1 text-xl font-semibold">{s.totals.marginPct}%</div></Card>
        </div>
      )}

      {tab === 'cash-flow' && s?.totals && (
        <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
          {[['Operating', s.totals.operatingMinor], ['Investing', s.totals.investingMinor], ['Financing', s.totals.financingMinor], ['Movement in cash', s.totals.actualMovementMinor]].map(([l, v]) => (
            <Card key={String(l)} className="p-4"><div className="text-xs text-ink-500">{l}</div><div className="mt-1 text-xl font-semibold tabular-nums"><Money minor={v as number} currency={s.currency} /></div></Card>
          ))}
        </div>
      )}

      {(tab === 'ar-aging' || tab === 'ap-aging') && s?.totals && (
        <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-5">
          {Object.entries(s.totals).filter(([k]) => k !== 'outstandingMinor').map(([bucket, v]) => (
            <Card key={bucket} className="p-4"><div className="text-xs text-ink-500">{bucket}</div><div className="mt-1 text-lg font-semibold tabular-nums"><Money minor={v} currency={s.currency} /></div></Card>
          ))}
        </div>
      )}

      {tab === 'pnl' && trend.data && trend.data.rows?.length > 0 && (
        <Card className="mb-4 p-4" title="Monthly">
          <ReportChart
            result={{ key: 'pnlTrend', title: 'Monthly', currency: trend.data.currency, columns: [{ key: 'month', label: 'Month', type: 'date' }, { key: 'incomeMinor', label: 'Income', type: 'money' }, { key: 'expenseMinor', label: 'Expenses', type: 'money' }, { key: 'netMinor', label: 'Net', type: 'money' }], rows: trend.data.rows }}
            chartType="area" mapping={{ x: 'month' }} height={220}
          />
        </Card>
      )}

      {s && <Card title={s.title}><ReportChart result={s} chartType="table" /></Card>}
      {stmt.isError && <p className="text-sm text-red-600">{(stmt.error as Error).message}</p>}
    </div>
  );
}
